import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { setCurrentQuestionInfo } from '@actions/quizTaking';
import {
  getAllQuestionsBySection,
  getCurrentQuestionInfo,
  getQuizDetails
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import useOngoingSection from '@cont/Quiz/Taking/Common/useOngoingSection';
import { NTA_COLORS, NTA_PAGE_PADDING_X } from '../constants';
import { ntaPageCss } from '../typography';
import NtaSectionChip from './NtaSectionChip';

const BarWrap = styled.div`
  padding-top: 8px;
  width: 100%;
  background: ${NTA_COLORS.white};
`;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px ${NTA_PAGE_PADDING_X} 0;
  ${ntaPageCss}
`;

const NtaSectionBar = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const { sectionUID } = useSelectorWithShallowEqual(getCurrentQuestionInfo);
  const questionsBySection = useSelectorWithShallowEqual(
    getAllQuestionsBySection
  );
  const { isLocked, hasOngoingSectional, onGoingSectionUID } =
    useOngoingSection(sectionUID);

  const sections = quizDetails?.sections ?? [];
  const showBar = sections.length > 1 && !sections[0]?.is_default;

  const [activeSectionUID, setActiveSectionUID] = useState(
    sectionUID ?? sections[0]?.uid
  );

  useEffect(() => {
    if (sectionUID) {
      setActiveSectionUID(sectionUID);
    }
  }, [sectionUID]);

  useEffect(() => {
    if (!hasOngoingSectional || !isLocked || !onGoingSectionUID) return;

    const firstQuestionUID = questionsBySection[onGoingSectionUID]?.allIds?.[0];
    if (!firstQuestionUID) return;

    dispatch(
      setCurrentQuestionInfo({
        sectionUID: onGoingSectionUID,
        questionUID: firstQuestionUID
      })
    );
  }, [
    dispatch,
    hasOngoingSectional,
    isLocked,
    onGoingSectionUID,
    questionsBySection
  ]);

  if (!showBar) return null;

  const handleSectionClick = (uid) => {
    setActiveSectionUID(uid);
    const firstQuestionUID = questionsBySection[uid]?.allIds?.[0];
    if (!firstQuestionUID) return;

    dispatch(
      setCurrentQuestionInfo({
        sectionUID: uid,
        questionUID: firstQuestionUID
      })
    );
  };

  return (
    <BarWrap>
      <Bar>
        {sections.map((section, index) => (
          <NtaSectionChip
            key={section.uid}
            section={section}
            index={index}
            isActive={section.uid === activeSectionUID}
            onSectionClick={handleSectionClick}
          />
        ))}
      </Bar>
    </BarWrap>
  );
};

export default NtaSectionBar;
